import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Alert } from 'react-bootstrap';
import { useUI } from '../context/UIContext';
import { benefitService } from '../services/benefitService';
import { ROUTES } from '../constants/routes';
import BenefitUpdateForm from '../components/benefit/BenefitUpdateForm';
import BenefitHistoryTable from '../components/benefit/BenefitHistoryTable';

const BenefitUpdatePage = () => {
  const navigate = useNavigate();
  const { showLoading, hideLoading, showError, hideAlert } = useUI();

  const [formData, setFormData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFundType, setSelectedFundType] = useState('');

  useEffect(() => {
    const loadFormData = async () => {
      showLoading('Loading benefit form...');
      try {
        const response = await benefitService.getFormData();
        if (response.success) {
          setFormData(response);
        } else {
          setLoadError(response.error || 'Failed to load benefit form data.');
        }
      } catch (err) {
        console.error('Error loading benefit form data:', err);
        setLoadError('An error occurred while loading benefit form data.');
      } finally {
        hideLoading();
      }
    };
    loadFormData();
  }, [showLoading, hideLoading]);

  if (!formData && !loadError) {
    // Display nothing when data is not ready
    return <></>;
  }

  const handleBackToCustomer = () => {
    if (formData?.accountId) {
      navigate(`/customer/${formData.accountId}`);
    } else {
      navigate(ROUTES.SEARCH);
    }
  };

  const handleSubmit = async (benefitData) => {
    hideAlert();
    setIsSubmitting(true);

    try {
      const response = await benefitService.createBenefit(benefitData);
      if (response.success) {
        // Navigate back to customer detail page on success
        navigate(`/customer/${formData.accountId}`);
      } else {
        // Display validation errors
        if (response.validationErrors && response.validationErrors.length > 0) {
          showError(response.validationErrors.join(' '));
        } else {
          showError(response.error || 'Failed to create benefit.');
        }
        // Scroll to top to show errors
        window.scrollTo(0, 0);
      }
    } catch (err) {
      console.error('Error creating benefit:', err);
      showError('An unexpected error occurred. Please try again.');
      window.scrollTo(0, 0);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <>
        <Alert variant="danger">{loadError}</Alert>
        <Button variant="primary" onClick={() => navigate(ROUTES.SEARCH)}>
          Back to Search
        </Button>
      </>
    );
  }

  return (
    <Container id="benefit-update-page">
      <div className="d-flex justify-content-between mb-3">
        <Col md={2}>
          <Button variant="outline-secondary" onClick={handleBackToCustomer}>
            Back to Customer Details
          </Button>
        </Col>
        <Col md={7} offset="4">
          <h2>Update Benefit</h2>
        </Col>
        </div>

        <Row>
          {/* Left Column - Form */}
          <Col>
            {/* Benefit History */}
            <Card className="shadow mb-4">
              <Card.Header>
                <h3 className="mb-0">Benefit History</h3>
              </Card.Header>
              <Card.Body>
                <BenefitHistoryTable
                  benefits={formData?.existingBenefits || []}
                  appliedPledgeTypes={formData?.appliedPledgeTypes}
                />
              </Card.Body>
            </Card>

            {/* Benefit Form */}
            <Card className="shadow mb-4">
              <Card.Header>
                <h3 className="mb-0">Update Benefit - {formData.accountId}</h3>
              </Card.Header>
              <Card.Body>
                <BenefitUpdateForm
                  formData={formData}
                  onSubmit={handleSubmit}
                  isSubmitting={isSubmitting}
                  onFundTypeChange={setSelectedFundType}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
    </Container>
  );
};

export default BenefitUpdatePage;
